import React from 'react';
import { 
  FaUser, FaLock, FaEnvelope, FaPhone, FaCalendarAlt, FaHashtag, 
  FaDollarSign, FaEdit, FaTextHeight, FaGlobe, FaCity, FaBuilding, 
  FaCog, FaList, FaTags, FaFlag, FaMapMarkerAlt, FaIndustry,
  FaUserTie, FaGraduationCap, FaHeart, FaHome, FaCar, FaPlane,
  FaShoppingCart, FaChartBar, FaFileAlt, FaSearch, FaFilter,
  FaHandHoldingUsd, FaHospital, FaBalanceScale, FaCalculator
} from 'react-icons/fa';
import './drop-down.css';

/**
 * Generates a drop-down select component (modernized version).
 * @param {Object} props Component properties.
 * @returns Generated component.
 */
const DropDown = (props) => {
  const {
    label,
    icon,
    placeholder = 'Sélectionnez une option',
     hidePlaceholder = false,
    value = '',
    onChange,
    options = [],
    disabled = false,
    xsmall,
    small,
    normal,
    large,
    xlarge,
    xxlarge,
    className = '',
    ...otherProps
  } = props;

  // Fonction pour convertir les valeurs string en leur type original
  const handleChange = (event) => {
    if (!onChange) return;
    
    const stringValue = event.target.value;
    
    // Chercher l'option correspondante pour récupérer sa valeur originale
    const findOriginalValue = (opts) => {
      for (const option of opts) {
        if (option.options && Array.isArray(option.options)) {
          // Cas des optgroups
          const found = findOriginalValue(option.options);
          if (found !== undefined) return found;
        } else {
          // Option simple
          const optionValue = typeof option === 'object' ? option.value : option;
          if (String(optionValue) === stringValue) {
            return optionValue;
          }
        }
      }
      return stringValue; // Retourne la valeur string par défaut si pas trouvée
    };

    const originalValue = findOriginalValue(options);
    
    // Créer un nouvel event avec la valeur originale
    const syntheticEvent = {
      ...event,
      target: {
        ...event.target,
        value: originalValue
      }
    };
    
    onChange(syntheticEvent);
  };

  // Gestion des classes de taille
  const getSizeClass = () => {
    if (xsmall) return 'xsmall';
    if (small) return 'small';
    if (normal) return 'normal';
    if (large) return 'large';
    if (xlarge) return 'xlarge';
    if (xxlarge) return 'xxlarge';
    return ''; // Pas de classe de taille = largeur par défaut
  };

  const sizeClass = getSizeClass();

  // Fonction pour obtenir l'icône par défaut selon le label
  const getDefaultIcon = () => {
    if (icon) return icon; // Si une icône est fournie, l'utiliser
    
    if (!label) return <FaList />; // Icône par défaut
    
    const labelLower = label.toLowerCase();
    
    // Icônes basées sur le contenu du label
    // Géographie et lieux
    if (labelLower.includes('pays') || labelLower.includes('country')) return <FaGlobe />;
    if (labelLower.includes('ville') || labelLower.includes('city') || labelLower.includes('commune')) return <FaCity />;
    if (labelLower.includes('région') || labelLower.includes('region') || labelLower.includes('département')) return <FaMapMarkerAlt />;
    if (labelLower.includes('adresse') || labelLower.includes('address') || labelLower.includes('domicile')) return <FaHome />;
    
    // Personnes et identité
    if (labelLower.includes('utilisateur') || labelLower.includes('user') || labelLower.includes('nom') || labelLower.includes('prénom')) return <FaUser />;
    if (labelLower.includes('civilité') || labelLower.includes('titre') || labelLower.includes('genre')) return <FaUserTie />;
    
    // Contact
    if (labelLower.includes('email') || labelLower.includes('mail') || labelLower.includes('courriel')) return <FaEnvelope />;
    if (labelLower.includes('téléphone') || labelLower.includes('phone') || labelLower.includes('mobile')) return <FaPhone />;
    
    // Dates et temps
    if (labelLower.includes('date') || labelLower.includes('année') || labelLower.includes('mois')) return <FaCalendarAlt />;
    
    // Professionnel
    if (labelLower.includes('entreprise') || labelLower.includes('société') || labelLower.includes('company')) return <FaBuilding />;
    if (labelLower.includes('secteur') || labelLower.includes('industrie') || labelLower.includes('activité')) return <FaIndustry />;
    if (labelLower.includes('profession') || labelLower.includes('métier') || labelLower.includes('job')) return <FaUserTie />;
    if (labelLower.includes('formation') || labelLower.includes('diplôme') || labelLower.includes('étude')) return <FaGraduationCap />;
      // Spécifique au domaine notarial et retraite
    if (labelLower.includes('prestation de serment') || labelLower.includes('serment')) return <FaBalanceScale />;
    if (labelLower.includes('régime de colmar') || labelLower.includes('colmar et metz') || labelLower.includes('colmar') || labelLower.includes('metz')) return <FaMapMarkerAlt />;
    if (labelLower.includes('classe retraite') || labelLower.includes('classe')) return <FaTags />;
    if (labelLower.includes('moyenne des produits') || labelLower.includes('produits de l\'étude') || (labelLower.includes('moyenne') && labelLower.includes('produits'))) return <FaChartBar />;
    
    // Financier
    if (labelLower.includes('prix') || labelLower.includes('montant') || labelLower.includes('euro') || labelLower.includes('€')) return <FaDollarSign />;
    if (labelLower.includes('salaire') || labelLower.includes('revenu') || labelLower.includes('rémunération')) return <FaDollarSign />;
    if (labelLower.includes('versement') || labelLower.includes('prestations') || labelLower.includes('paiement')) return <FaHandHoldingUsd />;
    if (labelLower.includes('avantages en nature') || labelLower.includes('avantage en nature')) return <FaHeart />;
    if (labelLower.includes('franchise') || labelLower.includes('hospitalisation') || labelLower.includes('médical') || labelLower.includes('santé')) return <FaHospital />;
    if (labelLower.includes('barème') || labelLower.includes('invalidité')) return <FaBalanceScale />;
    if (labelLower.includes('calcul') || labelLower.includes('formule') || labelLower.includes('rente')) return <FaCalculator />;
    
    // Fiscal
    if (labelLower.includes('régime fiscal') || labelLower.includes('fiscal') || labelLower.includes('impôt') || labelLower.includes('taxe')) return <FaBalanceScale />;
    
    // Configuration et paramètres
    if (labelLower.includes('config') || labelLower.includes('paramètre') || labelLower.includes('option')) return <FaCog />;
    if (labelLower.includes('statut') || labelLower.includes('état') || labelLower.includes('status')) return <FaTags />;
    if (labelLower.includes('type') || labelLower.includes('catégorie') || labelLower.includes('genre')) return <FaTags />;
    if (labelLower.includes('priorité') || labelLower.includes('niveau') || labelLower.includes('grade')) return <FaFlag />;
    
    // Transport
    if (labelLower.includes('véhicule') || labelLower.includes('voiture') || labelLower.includes('transport')) return <FaCar />;
    if (labelLower.includes('voyage') || labelLower.includes('destination') || labelLower.includes('vol')) return <FaPlane />;
    
    // Autres
    if (labelLower.includes('produit') || labelLower.includes('article') || labelLower.includes('commande')) return <FaShoppingCart />;
    if (labelLower.includes('rapport') || labelLower.includes('statistique') || labelLower.includes('analyse')) return <FaChartBar />;
    if (labelLower.includes('document') || labelLower.includes('fichier') || labelLower.includes('file')) return <FaFileAlt />;
    if (labelLower.includes('recherche') || labelLower.includes('search') || labelLower.includes('chercher')) return <FaSearch />;
    if (labelLower.includes('filtre') || labelLower.includes('filter') || labelLower.includes('tri')) return <FaFilter />;
    if (labelLower.includes('favori') || labelLower.includes('préférence') || labelLower.includes('like')) return <FaHeart />;
    
    // Arbre/hiérarchie - pour les listes avec sous-options
    if (options.some(opt => opt.options)) return <FaList />;
    
    // Icône par défaut pour les listes simples
    return <FaList />;
  };

  const defaultIcon = getDefaultIcon();
  return (
    <div className={`drop-down-container ${sizeClass} ${className}`.trim()}>
      {label && (
        <div className="drop-down-label-row">
          {defaultIcon && <span className="drop-down-icon">{defaultIcon}</span>}
          <label className="drop-down-label">{label}</label>
        </div>
      )}<select
        className="drop-down-select"
        value={value}
        onChange={handleChange}
        disabled={disabled}
        {...otherProps}
      >
        {!hidePlaceholder && placeholder && <option value="" disabled>{placeholder}</option>}
        {options.map((option, idx) => {
          // Si l'option a des sous-options (groupe)
          if (option.options && Array.isArray(option.options)) {
            return (
              <optgroup key={idx} label={option.label}>
                {option.options.map((subOption, subIdx) => (
                  <option key={`${idx}-${subIdx}`} value={typeof subOption === 'object' ? subOption.value : subOption}>
                    {typeof subOption === 'object' ? subOption.label : subOption}
                  </option>
                ))}
              </optgroup>
            );
          }
          // Option simple
          return (
            <option key={idx} value={typeof option === 'object' ? option.value : option}>
              {typeof option === 'object' ? option.label : option}
            </option>
          );
        })}
      </select>
    </div>
  );
};

export default DropDown;
